import React, { useCallback, useMemo } from 'react';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import {
    GetSongCollectionTiktokAudioStatsParams,
    SongCollectionTiktokAudioStats,
    ClientMonitoredTiktokAudioRelationship,
} from '@round/api';
import { Column } from 'react-table';
import cn from 'classnames';
import styles from './TiktokAudiosTable.module.css';
import Skeleton from '../../../../../ui/Skeleton/Skeleton';
import { formatNumberToKNotation, roundTo2Dp } from '../../../../../helpers';
import TruncatedTextCell from '../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import BarsSmallChart from '../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import TiktokAudioBadge from '../../../../TikTok/audios/components/TiktokAudioBadge/TiktokAudioBadge';

export type TiktokAudiosTableRow = SongCollectionTiktokAudioStats & {
    imageUrl: string | null;
    audioUrl: string | null;
    clientMonitoredAudioRelation: ClientMonitoredTiktokAudioRelationship[];
};

type TiktokAudiosTableProps = Omit<PaginationTableProps<TiktokAudiosTableRow>, 'columns'> & {
    ordering?: GetSongCollectionTiktokAudioStatsParams['ordering'];
};

const TiktokAudiosTable = ({ ordering, ...props }: TiktokAudiosTableProps) => {
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (ordering?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [ordering]
    );

    const columns: Column<TiktokAudiosTableRow>[] = useMemo(
        () => [
            {
                Header: 'Sound',
                accessor: 'title',
                Cell: ({ row: { original } }) => (
                    <TiktokAudioBadge
                        loading={props.loading}
                        audio={{
                            trackingType: original.tracking_type,
                            isOriginal: original.is_original,
                            imageUrl: original.imageUrl ?? '',
                            tiktokId: original.tiktok_id,
                            clientMonitoredAudioRelationships: original.clientMonitoredAudioRelation,
                            title: original.title,
                            audioPlayUrl: original.audioUrl ?? '',
                        }}
                    />
                ),
            },
            {
                Header: 'Author',
                accessor: 'author_name',
                Cell: ({ value }) =>
                    props.loading ? (
                        <Skeleton width="75%" />
                    ) : (
                        <TruncatedTextCell className={styles.authorName}>{value}</TruncatedTextCell>
                    ),
            },
            {
                Header: 'Video',
                accessor: 'video_count',
                Cell: ({ value, row: { original } }) => {
                    if (props.loading) {
                        return <Skeleton width="75%" />;
                    }

                    const badgeValue =
                        ordering !== '-video_count'
                            ? Number(original[ordering?.slice(1) as keyof SongCollectionTiktokAudioStats])
                            : null;

                    return (
                        <>
                            <span className={styles.videoCount}>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValue === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValue >= 0,
                                        [styles.decreasing]: badgeValue < 0,
                                    })}
                                >
                                    {badgeValue > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValue)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'standardised_data',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.dailyGrowth}>
                            <BarsSmallChart
                                values={value.daily_video_counts.slice(-7).map((i) => ({
                                    timestamp: i.timestamp,
                                    value: i.change,
                                }))}
                                color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                            />
                        </div>
                    );
                },
            },
        ],
        [formatVideoCountBadgeValue, ordering, props.loading]
    );

    return (
        <PaginationTable
            columns={columns}
            tableClassName={styles.table}
            tbodyClassName={styles.tbody}
            autoResetPage={false}
            {...props}
        />
    );
};

export default TiktokAudiosTable;
